import { makeStyles } from '@mui/styles';
import { Typography } from 'apollo-react';
import Button from 'apollo-react/components/Button';
import get from 'lodash/get';
import trim from 'lodash/trim';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import LoginHeading from './LoginHeading';
import Loader from './LoginLoader';
import { fetchLoginMethod, login, setSSOLoginFailure } from '../../../actions/AuthActions';
import LOGIN_METHODS from '../../../constants/loginMethodsConstants';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import {
  selectIsSSOLoginFailure,
  selectLoginMethod,
  selectShowFEDSSOPopup,
} from '../../../selectors/AuthSelectors';
import { getEnvVar } from '../../../utils/env';
import { isNilOrEmpty } from '../../../utils/ramdaUtils';
import { focusSSOPopup } from '../../../utils/ssoPopupUtil';
import FormHelperText from '../Form/FormHelperText';
import InputField from '../Form/InputField';
import PasswordInput from '../Form/PasswordInput';

const useStyles = makeStyles({
  button: {
    width: '100%',
    '& > div': {
      overflow: 'hidden',
    },
  },
});

const getMappedDomain = getEnvVar('URL_TO_TENANT_MAPPING') ?? {};

const LOGIN_SCREENS = {
  PASSWORD_SCREEN: 'PASSWORD_SCREEN',
  USERNAME_SCREEN: 'USERNAME_SCREEN',
};

const reauthenticateMessage = 'Your session has expired. Please reauthenticate to continue.';

const getSubdomainInfo = (url) => {
  const lowerCaseUrl = url.toLowerCase();
  const searchUrl = new URL(lowerCaseUrl);
  const hostName = searchUrl.hostname;
  if (localStorage.getItem('tenant') && localStorage.getItem('tenant') !== 'null') {
    return `${localStorage.getItem('tenant')}`;
  } else {
    return getMappedDomain[hostName] ?? getMappedDomain.default;
  }
};

const LoginAccessForm = ({
  isSSOLoginFailure,
  setSSOLoginFailureAction,
  fetchLoginMethodAction,
  pending,
  loginMethod,
  loginAction,
  loginError,
  authError,
  hasError,
  showSSOPopup,
  tenant,
  isTokenExpired,
}) => {
  const classes = useStyles();
  const translateString = useTranslation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [currentScreen, setCurrentScreen] = useState(LOGIN_SCREENS.USERNAME_SCREEN);

  useEffect(() => {
    document.title = isTokenExpired ? 'Reauthenticate' : 'Login';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTimeout(() => {
      localStorage.setItem('autoAuthentication', true);
    }, 500);
    const lowerCaseUrl = window.location.href.toLowerCase();
    const searchUrl = new URL(lowerCaseUrl);
    const domain = searchUrl.searchParams.get('domain');
    if (
      (tenant && tenant !== 'null' && localStorage.getItem('autoAuthentication') === 'true') ||
      domain != null
    ) {
      fetchLoginMethodAction(trim('@' + tenant), true);
    }
    if (isSSOLoginFailure) {
      setSSOLoginFailureAction(false);
      setCurrentScreen(LOGIN_SCREENS.USERNAME_SCREEN);
      setUsername('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSSOLoginFailure]);

  useEffect(() => {
    if (!loginError && [LOGIN_METHODS.WINNT, LOGIN_METHODS.LDAP].includes(loginMethod)) {
      setCurrentScreen(LOGIN_SCREENS.PASSWORD_SCREEN);
    } else {
      setCurrentScreen(LOGIN_SCREENS.USERNAME_SCREEN);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginMethod]);

  const isCurrentScreen = (screenToCompare = '') => {
    const existsScreen = LOGIN_SCREENS[screenToCompare];

    return existsScreen && currentScreen === screenToCompare;
  };

  const getBtnLoginLabel = () =>
    isCurrentScreen(LOGIN_SCREENS.USERNAME_SCREEN) ? 'Continue' : 'Log in';

  const verifyUserAccessType = async () => {
    if (showSSOPopup) {
      focusSSOPopup();
    } else {
      await fetchLoginMethodAction(trim(username));
    }
  };

  const loginAsWinNTUser = () =>
    loginAction({
      username: trim(username + (currentScreen === 'USERNAME_SCREEN' ? tenant : '')),
      password,
    });

  const handleUsernameChange = ({ target: { value } }) => setUsername(value);
  const handlePasswordChange = ({ target: { value } }) => setPassword(value);

  const handleLoginClick = () => {
    const { USERNAME_SCREEN, PASSWORD_SCREEN } = LOGIN_SCREENS;

    if (currentScreen === USERNAME_SCREEN) {
      verifyUserAccessType();
    }

    if (currentScreen === PASSWORD_SCREEN) {
      loginAsWinNTUser();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLoginClick();
  };

  const isPasswordScreen = isCurrentScreen(LOGIN_SCREENS.PASSWORD_SCREEN);

  return (
    <div className="login-form">
      <LoginHeading text={!isTokenExpired ? 'Log in' : 'Reauthenticate'} />
      {isTokenExpired && (
        <Typography variant="bodyDefault">{translateString(reauthenticateMessage)}</Typography>
      )}
      <form onSubmit={handleSubmit}>
        {(currentScreen || !isNilOrEmpty(tenant)) && (
          <span id="username" ui-ta="username" className="login-input">
            <InputField
              error={hasError}
              label="Username"
              value={username}
              onChange={handleUsernameChange}
              inputProps={{ autoComplete: 'off', autoFocus: true }}
            />
          </span>
        )}
        {isPasswordScreen && (
          <span id="password" ui-ta="password" className="login-input">
            <PasswordInput
              error={hasError}
              label="Password"
              type="password"
              value={password}
              width="100%"
              handleChange={handlePasswordChange}
              inputProps={{ autoFocus: isNilOrEmpty(tenant) || username }}
            />
          </span>
        )}
        {hasError && (
          <FormHelperText id="statusMessage" error>
            {authError}
          </FormHelperText>
        )}

        <Button
          type="submit"
          className={classes.button}
          color="primary"
          id="login-btn"
          ui-ta="login-btn"
          variant="primary"
          disabled={pending}
        >
          {pending ? <Loader /> : getBtnLoginLabel()}
        </Button>
      </form>
    </div>
  );
};

const mapStateToProps = (state) => {
  const loginError = get(state, 'auth.login_error', null);
  const pending = get(state, 'auth.pending', false);
  const hasError = !!loginError;
  const message = get(loginError, 'message', '');
  const status = get(loginError, 'status', '');
  const authError = `Error: ${message}${status ? ` Code: ${status}` : ''}`;
  const isSSOLoginFailure = selectIsSSOLoginFailure(state);
  const showSSOPopup = selectShowFEDSSOPopup(state);
  const loginMethod = selectLoginMethod(state);
  const tenant = getSubdomainInfo(window.location.href);
  const isTokenExpired = get(state, 'auth.isTokenExpired', false);

  return {
    authError,
    hasError,
    isSSOLoginFailure,
    showSSOPopup,
    pending,
    loginError,
    loginMethod,
    tenant,
    isTokenExpired,
  };
};

const mapDispatchToProps = {
  fetchLoginMethodAction: fetchLoginMethod,
  loginAction: login,
  setSSOLoginFailureAction: setSSOLoginFailure,
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginAccessForm);
